/**
 * WorkerGeofences — per-worker geofence overrides.
 *
 * Migrated to live API in Wave 3.2. Fetches /v1/attendance/geofences
 * filtered on worker_id (via `scope=worker` query). People
 * without an override follow their department's geofence or (if no
 * department override) the org-level default set on the Dashboard
 * widget's roster.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, DeleteOutlined, EditOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type { Geofence, GeofenceCreate } from '../../../api/attendance';
import type { Schema } from '../../../api/types';
import { ConfirmDialog } from '../../../components/primitives';
import GeofenceMapDialog, { GeofenceFormData } from './GeofenceMapDialog';
import GeofenceScopePriorityTip from './GeofenceScopePriorityTip';
import GeofenceRulesHint from './GeofenceRulesHint';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';

type Worker = Schema<'WorkerResponse'>;
type Department = Schema<'DepartmentResponse'>;

function initials(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

const WorkerGeofences: React.FC = () => {
  const { t } = useTranslation();
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Geofence | null>(null);
  const [workerId, setWorkerId] = useState('');
  const [geofenceName, setGeofenceName] = useState('');
  const [initialMapData, setInitialMapData] = useState<GeofenceFormData | undefined>();
  const [pendingDelete, setPendingDelete] = useState<Geofence | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [gs, es, ds] = await Promise.all([
        attendanceApi.geofencesApi.list('worker'),
        organizationApi.workersApi.list(),
        organizationApi.departmentsApi.list(),
      ]);
      setGeofences(gs.filter(g => g.worker_id));
      setWorkers(es);
      setDepartments(ds);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:workerGeofences.loadFailed'));
      setGeofences([]);
      setWorkers([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const workerById = useMemo(() => {
    const m = new Map<string, Worker>();
    for (const e of workers) m.set(e.id, e);
    return m;
  }, [workers]);
  const deptById = useMemo(() => {
    const m = new Map<string, Department>();
    for (const d of departments) m.set(d.id, d);
    return m;
  }, [departments]);

  const rows = useMemo(
    () =>
      geofences.map((g) => {
        const worker = workerById.get(g.worker_id ?? '');
        const empName = worker ? `${worker.first_name} ${worker.last_name}`.trim() : '—';
        const dept = worker ? deptById.get(worker.primary_department_id ?? '')?.name ?? '—' : '—';
        return { g, empName, dept };
      }),
    [geofences, workerById, deptById],
  );
  const filtered = useMemo(
    () => rows.filter(r =>
      r.empName.toLowerCase().includes(filter.toLowerCase()) ||
      r.dept.toLowerCase().includes(filter.toLowerCase()),
    ),
    [rows, filter],
  );
  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleOpen = (g?: Geofence) => {
    if (g) {
      setEditing(g);
      setWorkerId(g.worker_id ?? '');
      setGeofenceName(g.name);
      setInitialMapData({
        address: g.address ?? '',
        radius: g.radius_meters,
        lat: Number(g.lat),
        lng: Number(g.lng),
      });
    } else {
      setEditing(null);
      setWorkerId(workers[0]?.id ?? '');
      setGeofenceName('');
      setInitialMapData(undefined);
    }
    setDialogOpen(true);
  };

  const handleSave = async (map: GeofenceFormData) => {
    if (!workerId || !geofenceName.trim()) return;
    const payload: GeofenceCreate = {
      name: geofenceName,
      worker_id: workerId,
      lat: map.lat,
      lng: map.lng,
      radius_meters: map.radius,
      address: map.address,
    };
    try {
      if (editing) {
        await attendanceApi.geofencesApi.update(editing.id, payload);
        setToast(t('attendance:workerGeofences.overrideUpdated'));
      } else {
        await attendanceApi.geofencesApi.create(payload);
        setToast(t('attendance:workerGeofences.overrideAdded'));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:workerGeofences.saveFailed'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.geofencesApi.delete(pendingDelete.id);
      setToast(t('attendance:workerGeofences.overrideDeleted'));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:workerGeofences.deleteFailed'));
      throw e;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:workerGeofences.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:workerGeofences.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpen()} disabled={workers.length === 0}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('attendance:workerGeofences.addOverride')}</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <GeofenceScopePriorityTip current="worker" />

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" label={t('attendance:workerGeofences.filterLabel', { defaultValue: 'Filter by worker or department' })} value={filter}
          onChange={e => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 400, maxWidth: '100%', mr: 2 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]} />
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('attendance:timeEntries.colWorker')}</TableCell>
                <TableCell>{t('attendance:timeEntries.departmentLabel')}</TableCell>
                <TableCell>{t('attendance:workerGeofences.colName', { defaultValue: 'Geofence name' })}</TableCell>
                <TableCell>{t('attendance:workerGeofences.colRadius', { defaultValue: 'Radius (m)' })}</TableCell>
                <TableCell>{t('attendance:workerGeofences.colAddress', { defaultValue: 'Address' })}</TableCell>
                <TableCell align="center">{t('attendance:workerGeofences.colEnabled')}</TableCell>
                <TableCell align="center">{t('attendance:workerGeofences.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map(({ g, empName, dept }) => (
                <TableRow key={g.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>{initials(empName)}</Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{empName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{dept}</TableCell>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>{g.radius_meters}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{g.address ?? '—'}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={g.enabled ? t('common:answers.yes') : t('common:answers.no')} size="small"
                      sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', bgcolor: g.enabled ? 'success.light' : 'grey.200', color: g.enabled ? 'success.dark' : 'text.secondary' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton size="small" onClick={() => handleOpen(g)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" onClick={() => setPendingDelete(g)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paginated.length === 0 && !loading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">{filter ? t('attendance:workerGeofences.noMatches') : t('attendance:workerGeofences.noOverrides')}</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('attendance:workerGeofences.fallbackTip')}{' '}
        <Typography component="span" variant="caption" sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          onClick={() => (window.location.href = '/organization/workers/active')}>
          {t('attendance:workerGeofences.manageWorkers')}
        </Typography>
      </Typography>

      <GeofenceMapDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        title={editing ? t('attendance:workerGeofences.editOverride') : t('attendance:workerGeofences.addWorkerOverride')}
        subtitle={t('attendance:workerGeofences.mapSubtitle')}
        initialData={initialMapData}
        saveDisabled={!workerId || !geofenceName.trim()}
        saveLabel={editing ? t('attendance:workerGeofences.saveChanges') : t('attendance:workerGeofences.addOverride')}
        extraFields={
          <>
            <FormControl size="small" fullWidth disabled={!!editing}>
              <InputLabel>{t('attendance:workerGeofences.workerLabel')}</InputLabel>
              <Select value={workerId} label={t('attendance:workerGeofences.workerLabel')} onChange={e => setWorkerId(e.target.value)}>
                {workers.map(e => (
                  <MenuItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t('attendance:workerGeofences.geofenceNameLabel')} value={geofenceName} onChange={e => setGeofenceName(e.target.value)} size="small" fullWidth placeholder={t('attendance:workerGeofences.geofenceNamePlaceholder')} />
          </>
        }
      />

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:workerGeofences.deleteTitle')}
        description={t('attendance:workerGeofences.deleteDescription')}
        subject={pendingDelete && <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{pendingDelete.name}</Box>}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
      <GeofenceRulesHint />
    </Box>
  );
};

export default WorkerGeofences;
